import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../lib/auth';
import { staffActivitiesService, staffPartnersService, staffTagsService, staffVolunteersService, partnerActivitiesService } from '../../lib/api-service';
import type { StaffActivity } from '../../lib/types';
import { formatDate } from '../../utils/date';
import {
  Calendar,
  Tag,
  Plus,
  CheckCircle2,
  Clock,
  Handshake,
  Loader2,
  Eye,
  EyeOff,
  HandHelping,
} from 'lucide-react';

export default function StaffHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [totalActivities, setTotalActivities] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [totalVolunteers, setTotalVolunteers] = useState(0);
  const [recentActivities, setRecentActivities] = useState<StaffActivity[]>([]);

  const [greeting] = useState(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bună dimineața';
    if (hour < 18) return 'Bună ziua';
    return 'Bună seara';
  });

  // Staff (and super-admins) use the staff endpoints; partner-only users must
  // use their own scoped endpoints — the staff ones return 403 for them.
  const isStaff = !!user?.is_staff;

  useEffect(() => {
    async function loadStaff() {
      const [activitiesRes, partnersRes, volunteerStats] = await Promise.all([
        staffActivitiesService.list(),
        staffPartnersService.list(),
        staffVolunteersService.stats(),
      ]);
      const activities = activitiesRes.results;
      setTotalActivities(activitiesRes.count);
      setPublishedCount(activities.filter((a) => a.is_published).length);
      setDraftCount(activities.filter((a) => !a.is_published).length);
      setPartnerCount(partnersRes.count);
      setTotalVolunteers(volunteerStats.total_volunteers);
      // Most recent by start_datetime, take 5
      const sorted = [...activities].sort((a, b) => b.start_datetime.localeCompare(a.start_datetime));
      setRecentActivities(sorted.slice(0, 5));
    }

    async function loadPartner() {
      const activitiesRes = await partnerActivitiesService.list();
      const activities = activitiesRes.results;
      setTotalActivities(activitiesRes.count);
      setPublishedCount(activities.filter((a) => a.is_published).length);
      setDraftCount(activities.filter((a) => !a.is_published).length);
      const sorted = [...activities].sort((a, b) => b.start_datetime.localeCompare(a.start_datetime));
      setRecentActivities(sorted.slice(0, 5));
    }

    async function load() {
      try {
        await (isStaff ? loadStaff() : loadPartner());
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isStaff]);

  // Base path for activity links — staff manage all activities, partners only their own.
  const activityBasePath = isStaff ? '/staff/activitati' : '/staff/activitatile-mele';

  const quickActions = isStaff
    ? [
        { label: 'Activitate Nouă', icon: Plus, color: '#3b0b86', bgColor: '#f0e8ff', path: '/staff/activitati/nou' },
        { label: 'Gestionează Activități', icon: Calendar, color: '#01ffff', bgColor: '#e6ffff', path: '/staff/activitati' },
        { label: 'Gestionează Prieteni', icon: Handshake, color: '#ff6417', bgColor: '#fff3e6', path: '/staff/parteneri' },
        { label: 'Gestionează Tag-uri', icon: Tag, color: '#e1f433', bgColor: '#f5ffe6', path: '/staff/taguri' },
      ]
    : [
        { label: 'Activitate Nouă', icon: Plus, color: '#3b0b86', bgColor: '#f0e8ff', path: '/staff/activitatile-mele/nou' },
        { label: 'Activitățile Mele', icon: Calendar, color: '#01ffff', bgColor: '#e6ffff', path: '/staff/activitatile-mele' },
        { label: 'Organizațiile Mele', icon: Handshake, color: '#ff6417', bgColor: '#fff3e6', path: '/staff/organizatiile-mele' },
      ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font-primary)' }}>
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="mb-1 text-3xl font-bold">{greeting}, {user?.username}!</h1>
        <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Gestionează evenimentele Alba Iulia 2026
        </p>
      </div>

      {/* Stats Grid */}
      <div className={`mb-8 grid grid-cols-2 gap-4 ${isStaff ? 'lg:grid-cols-5' : 'lg:grid-cols-3'}`}>
        <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
          <Calendar className="mb-2 h-5 w-5 text-[var(--vivid-purple)]" />
          <p className="text-2xl font-bold text-[var(--vivid-purple)]">{totalActivities}</p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>Total Activități</p>
        </div>
        <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
          <CheckCircle2 className="mb-2 h-5 w-5 text-green-600" />
          <p className="text-2xl font-bold text-green-600">{publishedCount}</p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>Publicate</p>
        </div>
        <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
          <Clock className="mb-2 h-5 w-5 text-[var(--vivid-orange)]" />
          <p className="text-2xl font-bold text-[var(--vivid-orange)]">{draftCount}</p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>Draft</p>
        </div>
        {isStaff && (
          <>
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <Handshake className="mb-2 h-5 w-5 text-[var(--vivid-purple)]" />
              <p className="text-2xl font-bold text-[var(--vivid-purple)]">{partnerCount}</p>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>Prieteni</p>
            </div>
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <HandHelping className="mb-2 h-5 w-5 text-green-600" />
              <p className="text-2xl font-bold text-green-600">{totalVolunteers}</p>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>Voluntari</p>
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-bold">Acțiuni Rapide</h2>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="group flex flex-col items-center gap-3 rounded-2xl border bg-white p-6 transition-all hover:shadow-lg active:scale-95"
                style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}
              >
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-xl transition-transform group-hover:scale-110"
                  style={{ backgroundColor: action.bgColor }}
                >
                  <Icon className="h-7 w-7" style={{ color: action.color, stroke: action.color === '#e1f433' ? '#3b0b86' : action.color }} />
                </div>
                <span className="text-center text-sm font-semibold">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent Activities */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Activități Recente</h2>
          <button
            onClick={() => navigate(activityBasePath)}
            className="text-sm font-semibold text-[var(--vivid-purple)] transition-colors hover:underline"
          >
            Vezi Tot
          </button>
        </div>

        {recentActivities.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Nicio activitate încă.
          </p>
        ) : (
          <div className="space-y-3">
            {recentActivities.map((activity) => (
              <div
                key={activity.id}
                onClick={() => navigate(`${activityBasePath}/${activity.id}`)}
                className="cursor-pointer rounded-2xl border bg-white p-4 transition-all hover:shadow-lg active:scale-[0.98]"
                style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="mb-2 font-semibold">{activity.title}</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      {activity.is_published ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-semibold text-green-700">
                          <Eye className="h-3 w-3" /> Publicat
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                          <EyeOff className="h-3 w-3" /> Draft
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                        {formatDate(activity.start_datetime)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
